// EmailsTab -- Outlook email history with one account, pulled from the
// team's mailboxes by server/lib/outlookSync.js. Grouped into
// conversations, newest conversation first; open one to read the
// back-and-forth oldest to newest.

import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext.jsx';
import { buildSiteMatchers, matchSites, categorize, CATEGORIES } from '../lib/emailSorting.js';

function fmt(ts) {
  const d = new Date(ts);
  const today = new Date();
  const same = d.toDateString() === today.toDateString();
  return same
    ? `Today ${d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`
    : d.toLocaleDateString([], { year: 'numeric', month: 'short', day: 'numeric' })
      + ' ' + d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

const cleanSubject = (s) => (s || '(no subject)').replace(/^\s*((re|fw|fwd)\s*:\s*)+/i, '').trim() || '(no subject)';
const who = (m) => m.from_name || m.from_address || 'Unknown';

function Body({ id }) {
  const [html, setHtml] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    axios.get(`/api/customer-emails/${id}`)
      .then((r) => setHtml(r.data.body_html || `<pre>${(r.data.preview || '').replace(/</g, '&lt;')}</pre>`))
      .catch((e) => setErr(e.response?.data?.error || e.message));
  }, [id]);
  if (err) return <p className="calls-error">{err}</p>;
  if (html == null) return <p className="calls-dim">Loading…</p>;
  // Sandboxed: no scripts, no access to Command. Links open in a new tab.
  const doc = `<base target="_blank"><style>body{font-family:system-ui,sans-serif;font-size:14px;margin:12px;color:#1f2937}</style>${html}`;
  return <iframe className="em-body" title="Email" sandbox="allow-popups allow-popups-to-escape-sandbox" srcDoc={doc} />;
}

function fmtSize(n) {
  if (!n) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1048576) return `${Math.round(n / 1024)} KB`;
  return `${(n / 1048576).toFixed(1)} MB`;
}

// Attachments are fetched live from Outlook when the email is opened.
function Attachments({ id }) {
  const [list, setList] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    axios.get(`/api/customer-emails/${id}/attachments`)
      .then((r) => setList(r.data.found ? r.data.attachments : []))
      .catch((e) => setErr(e.response?.data?.error || e.message));
  }, [id]);

  async function open(a) {
    // Open the tab first (popup blockers allow it on click), then fill it.
    const w = window.open('', '_blank');
    try {
      const r = await axios.get(`/api/customer-emails/${id}/attachments/${encodeURIComponent(a.id)}`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      if (w) w.location.href = url; else window.location.href = url;
    } catch (e) {
      if (w) w.close();
      setErr(`Could not open ${a.name}`);
    }
  }

  async function download(a) {
    try {
      const r = await axios.get(`/api/customer-emails/${id}/attachments/${encodeURIComponent(a.id)}`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const link = document.createElement('a');
      link.href = url; link.download = a.name || 'attachment';
      document.body.appendChild(link); link.click(); link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (e) {
      setErr(`Could not download ${a.name}`);
    }
  }

  if (err) return <p className="calls-error em-atts">{err}</p>;
  if (!list) return <p className="calls-dim em-atts">Loading attachments…</p>;
  if (!list.length) return null;
  return (
    <div className="em-atts">
      {list.map((a) => (
        <span key={a.id} className="em-att">
          <button type="button" className="em-att-name" onClick={() => open(a)} title="Open">📎 {a.name}</button>
          <span className="em-att-size">{fmtSize(a.size)}</span>
          <button type="button" className="em-att-dl" onClick={() => download(a)} title="Download">⬇</button>
        </span>
      ))}
    </div>
  );
}

export function Message({ m }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`em-msg em-${m.direction}`}>
      <button type="button" className="em-msg-head" onClick={() => setOpen(!open)}>
        <span className={`em-dir em-dir-${m.direction}`}>{{ out: 'Sent', in: 'Received', fwd: 'Forwarded', int: 'Internal' }[m.direction] || m.direction}</span>
        <span className="em-from">{who(m)}</span>
        <span className="em-to">to {(m.to_addresses || []).join(', ')}{m.cc_addresses?.length ? `, cc ${m.cc_addresses.join(', ')}` : ''}</span>
        <span className="em-when">{fmt(m.sent_at)}{m.has_attachments ? ' · 📎' : ''}</span>
      </button>
      {!open && m.preview && <div className="em-preview" onClick={() => setOpen(true)}>{m.preview}</div>}
      {open && (
        <>
          {m.has_attachments && <Attachments id={m.id} />}
          <Body id={m.id} />
          {m.web_link && <a className="em-outlook" href={m.web_link} target="_blank" rel="noreferrer">Open in Outlook</a>}
        </>
      )}
    </div>
  );
}

const PAGE = 40;
const monthLabel = (ts) => new Date(ts).toLocaleDateString([], { month: 'long', year: 'numeric' });

export default function EmailsTab({ contactId }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(null);
  const [openThread, setOpenThread] = useState(null);
  const [q, setQ] = useState('');
  const [urlParams] = useSearchParams();
  const [cat, setCat] = useState(urlParams.get('email_cat') || 'All');
  // ?email_site=ID (from a site's "Emails" link or Email Search) opens
  // the tab already narrowed to that site.
  const [siteId, setSiteId] = useState(urlParams.get('email_site') || '');
  const [person, setPerson] = useState('');
  const [shown, setShown] = useState(PAGE);

  const load = () => axios.get('/api/customer-emails', { params: { contact_id: contactId } })
    .then((r) => setData(r.data));

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setCat(urlParams.get('email_cat') || 'All'); setSiteId(urlParams.get('email_site') || ''); setPerson(''); setQ(urlParams.get('email_q') || ''); setShown(PAGE);
    axios.get('/api/customer-emails', { params: { contact_id: contactId } })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => { if (!cancelled) setError(e.response?.data?.error || e.message); });
    axios.get('/api/customer-emails/status').then((r) => { if (!cancelled) setStatus(r.data); }).catch(() => {});
    return () => { cancelled = true; };
  }, [contactId]);

  const rows = data?.rows || null;
  const sites = data?.sites || [];
  const siteById = useMemo(() => Object.fromEntries(sites.map((s) => [s.id, s])), [sites]);

  // Conversations, each tagged with sites, type and people.
  const allThreads = useMemo(() => {
    if (!rows) return [];
    const matchers = buildSiteMatchers(sites, data?.company);
    const map = new Map();
    for (const m of rows) {
      const k = m.conversation_id || `s:${cleanSubject(m.subject).toLowerCase()}`;
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(m);
    }
    return [...map.entries()].map(([key, msgs]) => {
      const subjects = [...new Set(msgs.map((m) => m.subject || ''))].join(' | ');
      const previews = msgs.map((m) => m.preview || '').join(' ');
      return {
        key,
        msgs: msgs.slice().reverse(),
        last: msgs[0],
        siteIds: matchSites(matchers, subjects, `${subjects} ${previews}`),
        category: categorize(subjects),
        people: [...new Set(msgs.map(who))]
      };
    });
  }, [rows, sites, data?.company]);

  // Sites and people that actually appear, with counts, for the dropdowns.
  const siteOptions = useMemo(() => {
    const n = {};
    for (const t of allThreads) for (const id of t.siteIds) n[id] = (n[id] || 0) + 1;
    return Object.entries(n)
      .map(([id, count]) => ({ id: Number(id), count, s: siteById[id] }))
      .filter((o) => o.s)
      .sort((a, b) => (a.s.site_name || '').localeCompare(b.s.site_name || ''));
  }, [allThreads, siteById]);
  const untaggedCount = allThreads.filter((t) => !t.siteIds.length).length;

  const personOptions = useMemo(() => {
    const n = {};
    for (const t of allThreads) for (const p of t.people) n[p] = (n[p] || 0) + 1;
    return Object.entries(n).sort((a, b) => b[1] - a[1]).slice(0, 40);
  }, [allThreads]);

  // Everything except the type filter, so the chips can show counts.
  const preCat = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return allThreads.filter((t) => {
      if (siteId === 'none' && t.siteIds.length) return false;
      if (siteId && siteId !== 'none' && !t.siteIds.includes(Number(siteId))) return false;
      if (person && !t.people.includes(person)) return false;
      if (needle && !t.msgs.some((m) =>
        [m.subject, m.preview, m.from_name, m.from_address, ...(m.to_addresses || [])]
          .some((v) => (v || '').toLowerCase().includes(needle)))) return false;
      return true;
    });
  }, [allThreads, q, siteId, person]);

  const catCounts = useMemo(() => {
    const n = { All: preCat.length };
    for (const c of CATEGORIES) n[c] = 0;
    for (const t of preCat) n[t.category]++;
    return n;
  }, [preCat]);

  const threads = cat === 'All' ? preCat : preCat.filter((t) => t.category === cat);
  const visible = threads.slice(0, shown);

  useEffect(() => { setShown(PAGE); }, [cat, siteId, person, q]);

  async function syncNow(reset) {
    await axios.post('/api/customer-emails/sync', { reset });
    setStatus((s) => ({ ...(s || {}), running: true }));
  }

  // While a sync runs, check every 5s; when it finishes, reload the list.
  useEffect(() => {
    if (!status?.running) return undefined;
    const t = setInterval(() => {
      axios.get('/api/customer-emails/status').then((r) => {
        setStatus(r.data);
        if (!r.data.running) load().catch(() => {});
      }).catch(() => {});
    }, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status?.running, contactId]);

  // Problems the server hit, per mailbox folder (shown to admins).
  const syncErrors = [];
  if (status?.last_error) syncErrors.push(status.last_error);
  for (const f of status?.folders || []) {
    if (f.last_error) syncErrors.push(`${f.mailbox} ${f.folder}: ${f.last_error}`);
  }
  for (const [mb, folders] of Object.entries(status?.last_result?.mailboxes || {})) {
    for (const [folder, r] of Object.entries(folders || {})) {
      const line = `${mb} ${folder}: ${r.error}`;
      if (r.error && !syncErrors.includes(line)) syncErrors.push(line);
    }
  }

  if (error) return <p className="calls-error">Could not load emails: {error}</p>;
  if (!rows) return <p className="calls-dim">Loading emails…</p>;

  const filtersOn = cat !== 'All' || siteId || person || q;
  let lastMonth = null;

  return (
    <div className="em-tab">
      <div className="calls-tab-summary">
        <span><strong>{rows.length}</strong> email{rows.length === 1 ? '' : 's'}</span>
        <span><strong>{allThreads.length}</strong> conversation{allThreads.length === 1 ? '' : 's'}</span>
        {status && !status.configured && <span className="calls-result-bad">Outlook sync isn't set up yet</span>}
        {status?.configured && status.last_run_at && <span>Last synced {fmt(status.last_run_at)}</span>}
        {user?.role === 'admin' && status?.configured && (
          <span className="em-admin">
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => syncNow(false)} disabled={status.running}>
              {status.running ? 'Syncing…' : 'Sync now'}
            </button>
            <button type="button" className="btn btn-secondary btn-sm" title="Re-read the whole history window, e.g. after adding new contact emails"
              onClick={() => syncNow(true)} disabled={status.running}>Rescan</button>
          </span>
        )}
      </div>

      {user?.role === 'admin' && syncErrors.length > 0 && (
        <div className="em-errors">
          <strong>Outlook sync problems</strong>
          {syncErrors.map((e) => <div key={e}>{e}</div>)}
        </div>
      )}

      {allThreads.length > 0 && (
        <div className="em-filters">
          <div className="em-chips">
            {['All', ...CATEGORIES].map((c) => (
              <button key={c} type="button" className={`em-chip${cat === c ? ' em-chip-on' : ''}`}
                onClick={() => setCat(c)} disabled={c !== 'All' && !catCounts[c] && cat !== c}>
                {c} <span className="em-chip-n">{catCounts[c] || 0}</span>
              </button>
            ))}
          </div>
          <div className="em-selects">
            {(siteOptions.length > 0 || siteId) && (
              <select value={siteId} onChange={(e) => setSiteId(e.target.value)}>
                <option value="">All sites</option>
                {siteOptions.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.s.site_name || o.s.site_number}{o.s.site_number && o.s.site_name ? ` (${o.s.site_number})` : ''} · {o.count}
                  </option>
                ))}
                {siteId && siteId !== 'none' && !siteOptions.some((o) => String(o.id) === String(siteId)) && siteById[siteId] && (
                  <option value={siteId}>{siteById[siteId].site_name || siteById[siteId].site_number} · 0</option>
                )}
                <option value="none">No site mentioned · {untaggedCount}</option>
              </select>
            )}
            {personOptions.length > 1 && (
              <select value={person} onChange={(e) => setPerson(e.target.value)}>
                <option value="">Anyone</option>
                {personOptions.map(([p, n]) => <option key={p} value={p}>{p} · {n}</option>)}
              </select>
            )}
            <input className="em-search" placeholder="Search emails" value={q} onChange={(e) => setQ(e.target.value)} />
            {filtersOn && (
              <button type="button" className="em-clear" onClick={() => { setCat('All'); setSiteId(''); setPerson(''); setQ(''); }}>Clear</button>
            )}
          </div>
        </div>
      )}

      {allThreads.length === 0 && <p className="calls-dim">No emails with this account yet.</p>}
      {allThreads.length > 0 && threads.length === 0 && <p className="calls-dim">No conversations match these filters.</p>}

      <div className="em-threads">
        {visible.flatMap((t) => {
          const isOpen = openThread === t.key;
          const month = monthLabel(t.last.sent_at);
          const header = month !== lastMonth ? <div className="em-month" key={`m-${month}`}>{month}</div> : null;
          lastMonth = month;
          return [
            header,
            <div key={t.key} className={`em-thread${isOpen ? ' em-thread-open' : ''}`}>
              <button type="button" className="em-thread-head" onClick={() => setOpenThread(isOpen ? null : t.key)}>
                <span className="em-subject">{cleanSubject(t.last.subject)}</span>
                {t.msgs.length > 1 && <span className="em-count">{t.msgs.length}</span>}
                <span className={`em-cat em-cat-${t.category.toLowerCase()}`}>{t.category}</span>
                {t.siteIds.slice(0, 2).map((id) => siteById[id] && (
                  <span key={id} className="em-site">{siteById[id].site_name || siteById[id].site_number}</span>
                ))}
                {t.siteIds.length > 2 && <span className="em-site">+{t.siteIds.length - 2}</span>}
                <span className="em-people">{t.people.join(', ')}</span>
                <span className="em-when">{fmt(t.last.sent_at)}</span>
              </button>
              {isOpen && <div className="em-msgs">{t.msgs.map((m) => <Message key={m.id} m={m} />)}</div>}
            </div>
          ];
        })}
      </div>

      {threads.length > shown && (
        <div className="em-more">
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShown(shown + PAGE)}>
            Show older ({threads.length - shown} more)
          </button>
        </div>
      )}
    </div>
  );
}
